import { useEffect, useState } from 'react';

import type { Candidate } from '@/models/candidate';
import type { Proposal } from '@/models/proposal';
import { getCandidateById } from '@/services/candidate-service';
import { getProposalSource, getProposalsForTopic } from '@/services/proposal-service';
import { useBrowserQueryCache } from '@/lib/query-cache/use-browser-query-cache';

import { toCandidateViewModel } from '@/adapters/candidate-adapter';
import type { TopicProposalItem } from '@/adapters/proposal-adapter';

type TopicProposalsState = {
  status: 'loading' | 'success' | 'error';
  proposals: TopicProposalItem[];
  error?: string;
};

type ProposalSourceState = {
  status: 'loading' | 'success' | 'error';
  candidate?: Candidate;
  proposal?: Proposal;
  error?: string;
};

export function useTopicProposals(topicId: string | null, candidates: Candidate[]): TopicProposalsState {
  const queryCache = useBrowserQueryCache();
  const [state, setState] = useState<TopicProposalsState>({
    status: 'loading',
    proposals: [],
  });

  useEffect(() => {
    if (!topicId || candidates.length === 0) {
      setState({ status: 'loading', proposals: [] });
      return;
    }

    let active = true;

    getProposalsForTopic(topicId, candidates, { cache: queryCache })
      .then((items) => {
        if (!active) {
          return;
        }

        setState({ status: 'success', proposals: items });
      })
      .catch((error) => {
        console.error('useTopicProposals failed', { topicId, error });
        if (!active) {
          return;
        }

        setState({
          status: 'error',
          proposals: [],
          error: 'Não foi possível carregar as propostas agora.',
        });
      });

    return () => {
      active = false;
    };
  }, [candidates, queryCache, topicId]);

  return state;
}

export function useProposalSource(
  candidateId: string | null,
  topicId: string | null,
): ProposalSourceState {
  const queryCache = useBrowserQueryCache();
  const [state, setState] = useState<ProposalSourceState>({ status: 'loading' });

  useEffect(() => {
    if (!candidateId || !topicId) {
      setState({ status: 'loading' });
      return;
    }

    let active = true;

    Promise.all([
      getCandidateById(candidateId, { cache: queryCache }),
      getProposalSource(candidateId, topicId, { cache: queryCache }),
    ])
      .then(([candidateDoc, proposal]) => {
        if (!active) {
          return;
        }

        const candidate = candidateDoc ? toCandidateViewModel(candidateDoc) : undefined;
        if (!candidate) {
          setState({
            status: 'error',
            error: 'Candidato não encontrado.',
          });
          return;
        }

        setState({
          status: 'success',
          candidate,
          proposal,
        });
      })
      .catch((error) => {
        console.error('useProposalSource failed', { candidateId, topicId, error });
        if (!active) {
          return;
        }

        setState({
          status: 'error',
          error: 'Não foi possível carregar a fonte agora.',
        });
      });

    return () => {
      active = false;
    };
  }, [candidateId, queryCache, topicId]);

  return state;
}
